import { useState } from 'react';
import { useTheme } from 'styled-components';

import { TableCell, Typography } from '@libs/components';
import { getChangePercentage, getDetailedFormatBalance } from '@libs/helpers';
import { formatDate, getDate, getTimeAgo } from '@libs/hooks';
import {
  CurrencySymbol,
  CurrencyType,
  HistoryPeriodEnumKeys,
} from '@libs/types';

import { useTranslation } from 'src/hooks';
import { CoinMarketCapAsset } from 'src/types';

import { useCoinDetailsTabs } from '../../hooks/useCoinDetailsTabs';
import CoinDynamicCard from '../coin-dynamic-card/CoinDynamicCard';

import {
  CoinCardsWrapper,
  ContentWrapper,
  CustomTabs,
  DincamicPriceHistoryWrapper,
  HistoyWrapper,
  Separator,
} from './styles';

const dateFormat = 'D MMM, YYYY';

type CoinPriceDynamicProps = {
  assetCode: CurrencyType;
  assetInfo?: CoinMarketCapAsset;
};
const CoinPriceDynamic: React.FC<CoinPriceDynamicProps> = ({
  assetCode,
  assetInfo,
}) => {
  const { t, selectedLanguageCode } = useTranslation();
  const { colors } = useTheme();

  const { shortPeriodTabs } = useCoinDetailsTabs(assetCode);

  const [activeDynamicPeriod, setActiveDynamicPeriod] = useState(
    shortPeriodTabs[0]
  );

  const currSymbol = CurrencySymbol.USD;

  const totalMinPrice = assetInfo?.minPrice;
  const totalMinPriceDate = assetInfo?.minPriceDate
    ? getDate(assetInfo.minPriceDate)
    : '';
  const totalMaxPrice = assetInfo?.maxPrice;
  const totalMaxPriceDate = assetInfo?.maxPriceDate
    ? getDate(assetInfo.maxPriceDate)
    : '';

  const totalMinChange = Math.round(
    getChangePercentage(assetInfo?.rate, totalMinPrice)
  );
  const totalMaxChange = Math.round(
    getChangePercentage(assetInfo?.rate, totalMaxPrice)
  );

  const maxPrice24h = assetInfo?.maxPrice24h;
  const maxPrice24hDate = assetInfo?.maxPrice24hDate;
  const minPrice24h = assetInfo?.minPrice24h;
  const minPrice24hDate = assetInfo?.minPrice24hDate;

  const maxPrice1m = assetInfo?.maxPrice1m;
  const minPrice1m = assetInfo?.minPrice1m;
  const minPrice1mDate = assetInfo?.minPrice1mDate;
  const maxPrice1mDate = assetInfo?.maxPrice1mDate;

  const maxPrice1y = assetInfo?.maxPrice1y;
  const minPrice1yDate = assetInfo?.minPrice1yDate;
  const minPrice1y = assetInfo?.minPrice1y;
  const maxPrice1yDate = assetInfo?.maxPrice1yDate;

  const prices = {
    [HistoryPeriodEnumKeys.ONE_DAY]: {
      minPrice: minPrice24h,
      minPriceDate: minPrice24hDate,
      maxPrice: maxPrice24h,
      maxPriceDate: maxPrice24hDate,
    },
    [HistoryPeriodEnumKeys.ONE_MONTH]: {
      minPrice: minPrice1m,
      minPriceDate: minPrice1mDate,
      maxPrice: maxPrice1m,
      maxPriceDate: maxPrice1mDate,
    },
    [HistoryPeriodEnumKeys.ONE_YEAR]: {
      minPrice: minPrice1y,
      minPriceDate: minPrice1yDate,
      maxPrice: maxPrice1y,
      maxPriceDate: maxPrice1yDate,
    },
  };

  const minPrice = prices[activeDynamicPeriod.id].minPrice;
  const maxPrice = prices[activeDynamicPeriod.id].maxPrice;

  const handlePeriodSelect = (id: string | number) => {
    const selectedPeriod = shortPeriodTabs.find((item) => item.id === id);
    if (selectedPeriod) setActiveDynamicPeriod(selectedPeriod);
  };

  const totalMaxChangeColor =
    totalMaxChange < 0 ? colors.text.critical : colors.text.success;

  const totalMinChangeColor =
    totalMinChange < 0 ? colors.text.critical : colors.text.success;

  const minPriceText = minPrice;
  const maxPriceText = maxPrice;

  const maxDescription = `${formatDate(
    dateFormat,
    totalMaxPriceDate
  )} (${getTimeAgo(totalMaxPriceDate, selectedLanguageCode)})`;

  const lowestDescription = `${formatDate(
    dateFormat,
    totalMinPriceDate
  )} (${getTimeAgo(totalMinPriceDate, selectedLanguageCode)})`;

  return (
    <ContentWrapper>
      <Typography type="subHeadline" fontFamily="primaryBold">
        {t('coin_details_tab_dynamics')}
      </Typography>

      <CustomTabs
        tabs={shortPeriodTabs}
        activeTab={activeDynamicPeriod.id}
        handleTabClick={handlePeriodSelect}
      />
      <CoinCardsWrapper>
        <CoinDynamicCard
          isPriceLow
          periodLabel={activeDynamicPeriod.title}
          amount={minPriceText + currSymbol}
        />
        <CoinDynamicCard
          periodLabel={activeDynamicPeriod.title}
          amount={maxPriceText + currSymbol}
        />
      </CoinCardsWrapper>

      <Typography type="subHeadline" fontFamily="primaryBold">
        {t('coin_details_history_price')}
      </Typography>

      <DincamicPriceHistoryWrapper>
        <HistoyWrapper>
          <TableCell
            titleColor={colors.text.secondary}
            titleSize="bodyButton"
            titleType="primaryMedium"
            descFont="primaryMedium"
            descType="caption"
            title={t('coin_details_dynamics_highest')}
            description={maxDescription}
          />
          <TableCell
            justifyToLeft={false}
            title={`${getDetailedFormatBalance(totalMaxPrice)}${currSymbol}`}
            description={
              <Typography color={totalMaxChangeColor}>
                {getDetailedFormatBalance(totalMaxChange, 0) + '%'}
              </Typography>
            }
          />
        </HistoyWrapper>
        <Separator />
        <HistoyWrapper>
          <TableCell
            titleColor={colors.text.secondary}
            titleSize="bodyButton"
            titleType="primaryMedium"
            descFont="primaryMedium"
            descType="caption"
            title={t('coin_details_dynamics_lowest')}
            description={lowestDescription}
          />
          <TableCell
            justifyToLeft={false}
            title={`${getDetailedFormatBalance(totalMinPrice)}${currSymbol}`}
            description={
              <Typography color={totalMinChangeColor}>
                {getDetailedFormatBalance(totalMinChange, 0) + '%'}
              </Typography>
            }
          />
        </HistoyWrapper>
      </DincamicPriceHistoryWrapper>
    </ContentWrapper>
  );
};

export default CoinPriceDynamic;
